import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { transactionService } from "../../services/api/Transactions";
import { GetTransactionByOrderIdData } from "../../services/models/GetTransactionByOrderId";
import {
  generateCryptoQrString,
  orderIdFromPaymentUrl,
} from "../../utils/generateCryptoQr";

interface CryptoDepositPopupProps {
  isOpen: boolean;
  paymentUrl: string;
  orderId?: string;
  onClose: () => void;
  onPaid: () => void;
}

function fiatPrefix(symbol?: string) {
  if (symbol === "CAD" || symbol === "USD") return "$";
  if (symbol === "BRL") return "R$ ";
  if (symbol === "EUR") return "€";
  return symbol ? `${symbol} ` : "";
}

function formatAmount(value?: number, digits = 2) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function networkLabel(network?: string) {
  const value = network || "";
  const lower = value.toLowerCase();
  if (lower.includes("polygon") || lower.includes("matic") || lower === "pol") {
    return { name: "Polygon (MATIC)", symbol: "MATIC" };
  }
  if (lower.includes("bsc") || lower.includes("bep")) {
    return { name: "BNB Smart Chain", symbol: "BSC" };
  }
  if (lower.includes("eth")) return { name: "Ethereum", symbol: "ETH" };
  return { name: value || "Network", symbol: value };
}

function statusLabel(status?: string) {
  const value = (status || "").toLowerCase();
  if (
    !value ||
    value === "pending" ||
    value === "waiting" ||
    value === "initiated" ||
    value === "processing"
  ) {
    return "Awaiting payment";
  }
  return status || "Awaiting payment";
}

function shortenId(value?: string) {
  if (!value) return "-";
  if (value.length <= 22) return value;
  return `${value.slice(0, 18)}...`;
}

function CopyIconButton({
  text,
  label,
}: {
  text: string;
  label: string;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={copied ? `${label} copied` : label}
      className="self-start shrink-0 h-[28px] px-[10px] rounded-full border border-border-subtle bg-bg-page-body text-text-base text-[11px] font-medium inline-flex items-center gap-[4px]"
    >
      <span className="material-symbols-outlined text-[14px]">
        {copied ? "check" : "content_copy"}
      </span>
      {copied ? "Copied" : "Copy address"}
    </button>
  );
}

function CryptoDepositPopup({
  isOpen,
  paymentUrl,
  orderId,
  onClose,
  onPaid,
}: CryptoDepositPopupProps) {
  const resolvedOrderId = orderId || orderIdFromPaymentUrl(paymentUrl);
  const [transaction, setTransaction] = useState<GetTransactionByOrderIdData>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timeLeft, setTimeLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !resolvedOrderId) {
      if (isOpen && !resolvedOrderId) {
        setError("This payment link does not include an order id.");
      }
      return;
    }

    let cancelled = false;

    const load = async (showLoader: boolean) => {
      try {
        if (showLoader) setLoading(true);
        const response = await transactionService.getTransactionByOrderId(
          resolvedOrderId,
        );
        if (cancelled) return;
        setTransaction(response.data);
        setError("");
      } catch (err: any) {
        if (cancelled) return;
        setError(
          err?.response?.data?.msg ||
            err?.message ||
            "Unable to load this deposit.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load(true);
    const pollId = window.setInterval(() => load(false), 10000);
    return () => {
      cancelled = true;
      window.clearInterval(pollId);
    };
  }, [isOpen, resolvedOrderId]);

  useEffect(() => {
    if (!isOpen || !transaction?.expiresAt) {
      setTimeLeft(null);
      return;
    }

    const tick = () => {
      const remaining = Math.floor(
        (new Date(transaction.expiresAt || "").getTime() - Date.now()) / 1000,
      );
      setTimeLeft(Math.max(remaining, 0));
    };

    tick();
    const timerId = window.setInterval(tick, 1000);
    return () => window.clearInterval(timerId);
  }, [isOpen, transaction?.expiresAt]);

  if (!isOpen) return null;

  const currency = transaction?.settledCurrency || "";
  const network = networkLabel(transaction?.network);
  const address = transaction?.depositAddress || "";
  const cryptoAmount = transaction?.settledAmountRequested;
  const fiatAmount = transaction?.baseAmount;
  const unitPrice =
    cryptoAmount && fiatAmount && cryptoAmount > 0
      ? fiatAmount / cryptoAmount
      : undefined;
  const qrValue = address
    ? generateCryptoQrString(
        address,
        transaction?.network || "",
        cryptoAmount,
        currency,
      )
    : "";
  const minutes = timeLeft == null ? "--" : String(Math.floor(timeLeft / 60)).padStart(2, "0");
  const seconds = timeLeft == null ? "--" : String(timeLeft % 60).padStart(2, "0");

  return (
    <div className="fixed inset-0 z-[1100] flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="crypto-deposit-title"
        className="relative w-full sm:max-w-[440px] max-h-[85vh] bg-bg-page-body rounded-t-[16px] sm:rounded-[16px] shadow-xl flex flex-col overflow-hidden mx-0 sm:mx-[16px]"
      >
        <div className="flex items-start justify-between gap-[12px] px-[16px] py-[14px] border-b border-border-subtle shrink-0">
          <div className="min-w-0">
            <h2
              id="crypto-deposit-title"
              className="text-text-base text-[16px] font-bold"
            >
              Deposit crypto
            </h2>
            <p className="text-text-subtle text-[12px] font-normal">
              {transaction?.baseCurrency
                ? `${transaction.baseCurrency} Crypto Wallet`
                : "Send crypto to complete this order"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close deposit"
            className="material-symbols-outlined text-[22px] text-text-subtle"
          >
            close
          </button>
        </div>

        <div className="px-[16px] py-[16px] overflow-y-auto space-y-[12px]">
          {loading && !transaction ? (
            <p className="py-[40px] text-center text-[14px] text-text-subtle">
              Loading deposit details...
            </p>
          ) : error && !transaction ? (
            <p className="py-[24px] text-center text-[12px] text-text-status-red">
              {error}
            </p>
          ) : (
            <>
              <div className="flex items-center justify-between gap-[8px]">
                <div className="flex items-center gap-[8px] min-w-0">
                  <span className="inline-flex items-center gap-[6px] h-[32px] px-[10px] rounded-full border border-border-subtle bg-bg-base text-text-base text-[12px] font-semibold">
                    {transaction?.settledCurrencyImage ? (
                      <img
                        src={transaction.settledCurrencyImage}
                        alt=""
                        className="h-[16px] w-[16px] rounded-full"
                      />
                    ) : null}
                    {currency || "Crypto"}
                  </span>
                  <span className="inline-flex items-center gap-[6px] h-[32px] px-[10px] rounded-full border border-border-subtle bg-bg-base text-text-base text-[12px] font-medium min-w-0">
                    {transaction?.networkImage ? (
                      <img
                        src={transaction.networkImage}
                        alt=""
                        className="h-[16px] w-[16px] rounded-full"
                      />
                    ) : null}
                    <span className="truncate">{network.name}</span>
                    <span className="text-text-subtle">{network.symbol}</span>
                  </span>
                </div>
                <span className="material-symbols-outlined text-[18px] text-text-subtle">
                  lock
                </span>
              </div>

              <div className="rounded-lg border border-border-subtle bg-bg-base p-[12px] flex gap-[12px]">
                <div className="shrink-0 rounded-lg bg-white p-[6px] border border-border-subtle">
                  {qrValue ? (
                    <QRCodeSVG value={qrValue} size={124} level="Q" />
                  ) : null}
                </div>
                <div className="min-w-0 flex-1 flex flex-col gap-[8px]">
                  <div>
                    <p className="text-[10px] font-normal text-text-subtle">
                      {network.name} Address
                    </p>
                    <p className="mt-[2px] text-[12px] font-medium text-text-base leading-[1.35] break-all">
                      {address || "-"}
                    </p>
                  </div>
                  <CopyIconButton text={address} label="Copy address" />
                  <div>
                    <p className="text-[10px] font-normal text-text-subtle">
                      Value
                    </p>
                    <p className="text-[14px] font-semibold text-text-base leading-tight">
                      {formatAmount(cryptoAmount)} {currency}
                    </p>
                    <p className="text-[12px] font-normal text-text-subtle">
                      = {fiatPrefix(transaction?.baseCurrency)}
                      {formatAmount(fiatAmount)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-border-subtle bg-bg-status-yellow px-[12px] py-[10px] text-[12px] leading-[1.4] text-text-status-yellow">
                Send only {currency || "the selected crypto"} via the{" "}
                {network.name} network. Any other currency or network = value
                lost.
              </div>

              <div className="rounded-full border border-border-subtle px-[12px] py-[8px] flex items-center justify-between text-text-base">
                <span className="inline-flex items-center gap-[8px] text-[12px] font-medium">
                  <span className="material-symbols-outlined text-[16px] text-text-subtle">
                    progress_activity
                  </span>
                  {statusLabel(transaction?.status)}
                </span>
                <span className="text-[12px] font-semibold tabular-nums">
                  {minutes}:{seconds}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-[8px]">
                <div className="rounded-lg border border-border-subtle bg-bg-base px-[12px] py-[10px]">
                  <p className="text-[10px] font-normal text-text-subtle">
                    Price
                  </p>
                  <p className="mt-[2px] text-[12px] font-semibold text-text-base">
                    1 {currency || "crypto"} ≈{" "}
                    {fiatPrefix(transaction?.baseCurrency)}
                    {formatAmount(unitPrice)}
                  </p>
                </div>
                <div className="rounded-lg border border-border-subtle bg-bg-base px-[12px] py-[10px]">
                  <p className="text-[10px] font-normal text-text-subtle">
                    Order
                  </p>
                  <div className="mt-[2px] flex items-center justify-between gap-[6px]">
                    <p className="text-[12px] font-semibold text-text-base truncate">
                      {shortenId(transaction?.orderId || resolvedOrderId)}
                    </p>
                    <button
                      type="button"
                      aria-label="Copy order id"
                      onClick={() =>
                        navigator.clipboard.writeText(
                          transaction?.orderId || resolvedOrderId,
                        )
                      }
                      className="shrink-0 text-text-subtle"
                    >
                      <span className="material-symbols-outlined text-[16px]">
                        content_copy
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          <button
            type="button"
            onClick={onPaid}
            className="w-full h-[44px] rounded-full text-white text-[14px] font-semibold"
            style={{
              background:
                "linear-gradient(90deg, #0E18A7 0%, #5E0EAF 51%, #AE0DB2 100%)",
            }}
          >
            I've already made the payment.
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full h-[44px] rounded-full text-[14px] font-semibold text-text-base border border-border-subtle bg-transparent"
          >
            Cancel deposit
          </button>
        </div>
      </div>
    </div>
  );
}

export default CryptoDepositPopup;
